interface Props {
  title?: string;
  height?: number;
  /** 'line' (default) shows diagonal shimmer lines; 'bar' shows upright bar columns. */
  variant?: 'line' | 'bar';
}

const CARD_CLASS =
  'rounded-[12px] border border-[color:var(--nq-line)] bg-[color:var(--nq-surface-card)] px-[18px] py-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.02)]';

const SKELETON = 'block rounded-[4px] bg-white/[0.06] animate-pulse';

const BAR_BASE =
  'flex-1 rounded-t-[3px] bg-white/[0.06] animate-pulse';

// Per-bar height + animation-delay kept as literal classes so Tailwind can
// statically detect the arbitrary values.
const BARS = [
  'h-[45%]',
  'h-[72%] [animation-delay:0.10s]',
  'h-[30%] [animation-delay:0.20s]',
  'h-[58%] [animation-delay:0.15s]',
  'h-[82%] [animation-delay:0.05s]',
  'h-[38%] [animation-delay:0.25s]',
  'h-[64%] [animation-delay:0.10s]',
  'h-[50%] [animation-delay:0.20s]',
  'h-[40%] [animation-delay:0.05s]',
  'h-[68%] [animation-delay:0.15s]',
];

const ComparisonChartSkeleton = ({
  title = 'Return overlay',
  height = 280,
  variant = 'line',
}: Props) => (
  <div className={CARD_CLASS} aria-busy="true">
    <div className="mb-3 flex flex-row flex-wrap items-end justify-between gap-3">
      <div>
        <h3 className="text-[1.05rem] font-semibold text-foreground">{title}</h3>
        <div className={`${SKELETON} h-3 w-[min(420px,85%)]`} />
      </div>
      <div className={`${SKELETON} h-7 w-[120px] rounded-[8px]`} />
    </div>
    <div
      className="relative mt-2 overflow-hidden rounded-[8px] border border-white/[0.05] bg-white/[0.02]"
      style={{ height }}
      aria-hidden
    >
      <div className="absolute inset-[16px_12px_28px_48px] opacity-60 bg-[size:100%_36px,72px_100%] bg-[linear-gradient(rgba(255,255,255,0.04)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.04)_1px,transparent_1px)]" />
      {variant === 'bar' ? (
        <div className="absolute inset-[16px_12px_28px_48px] flex items-end gap-1">
          {BARS.map((bar, i) => (
            <div key={i} className={`${BAR_BASE} ${bar}`} />
          ))}
        </div>
      ) : (
        <>
          <div className="absolute left-[48px] right-3 h-[3px] rounded-full bg-white/[0.06] animate-pulse top-[38%] rotate-[-4deg] opacity-90" />
          <div className="absolute left-[48px] right-3 h-[3px] rounded-full bg-white/[0.06] animate-pulse top-[58%] w-[78%] rotate-[3deg] opacity-65" />
        </>
      )}
    </div>
    <div className="m-0 flex flex-wrap gap-3.5 border-t border-white/[0.06] px-2 py-3">
      <div className={`${SKELETON} h-3 w-24`} />
      <div className={`${SKELETON} h-3 w-24`} />
      <div className={`${SKELETON} h-3 w-24`} />
    </div>
  </div>
);

export default ComparisonChartSkeleton;
